import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Mention, MentionsInput } from 'react-mentions';
import { useSearchParams } from 'react-router-dom';

import { DraftType, Jurisdiction } from '../../../../../../../../../../../backend/src/types/types';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { DocIds } from '@/api/types/backend-dto-types';
import Modal from '@/components/molecules/Modals/CustomModal';
import { capitalizeFirstLetter } from '@/helpers/filterNames';
import { Button } from '@/new/components/shadcn/button';
import { Select, SelectItem, SelectValue, SelectContent, SelectTrigger } from '@/new/components/shadcn/select';
import { MentionsInputStyleKim } from '@/styles';
import { ExecuteKimSearch, KimMode } from '@/types';

const CLAIM_DOC_TYPES = [
  'witness statement',
  'deposition',
  'court document',
  'transcript',
  'pleadings',
  'affidavit',
  'expert report',
  'deposition transcript',
];

enum DraftTypeOption {
  Claim = DraftType.Claim,
  Rebuttal = DraftType.Rebuttal,
  Deposition = DraftType.Deposition,
  WitnessStatement = 'witness statement',
}

interface MentionInterface {
  id: string;
  display: string;
  childIndex: number;
  index: number;
  plainTextIndex: number;
}

interface QuestionInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  executeSearch: ExecuteKimSearch;
  docs: DocIds[];
  taggedDocuments: { doc_id: string; file_name: string }[];
  setTaggedDocuments: (v: { doc_id: string; file_name: string }[]) => void;
  mode: KimMode;
  draftType: DraftType | null;
  setDraftType: (draftType: DraftType | null) => void;
  jurisdiction: Jurisdiction[];
  isCreatingThread: boolean;
}

export function QuestionInput({
  value,
  onChange,
  placeholder,
  executeSearch,
  docs,
  mode,
  taggedDocuments,
  setTaggedDocuments,
  draftType,
  setDraftType,
  jurisdiction,
  isCreatingThread,
}: QuestionInputProps) {
  const { t } = useTranslation();

  const [isFocused, setIsFocused] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const mentionsInputRef = useRef<any>(null);

  // Check search params
  const [searchParams] = useSearchParams();
  const selectedDoc = searchParams.get('selectedDocId');
  const processedDocRef = useRef<string | null>(null);

  const taggableDocs = useMemo(() => {
    if (draftType === DraftType.Deposition) {
      return docs.filter((doc) => doc.type && CLAIM_DOC_TYPES.includes(doc.type.toLowerCase()));
    }
    return docs;
  }, [draftType, docs]);

  const draftTypeOptions: { label: string; key: DraftTypeOption }[] = [
    { label: `${capitalizeFirstLetter(t('letter of claim'))} outline`, key: DraftTypeOption.Claim },
    { label: 'Letter of rebuttal outline', key: DraftTypeOption.Rebuttal },
    ...(jurisdiction.length === 0
      ? [
          { label: 'Deposition summary', key: DraftTypeOption.Deposition },
          { label: 'Witness statement summary', key: DraftTypeOption.WitnessStatement },
        ]
      : (jurisdiction
          .map((j) => {
            if (j === Jurisdiction.USFederal) {
              return { label: 'Deposition summary', key: DraftTypeOption.Deposition };
            }
            if (j === Jurisdiction.EnglandAndWales) {
              return { label: 'Witness statement summary', key: DraftTypeOption.WitnessStatement };
            }
          })
          .filter(Boolean) as { label: string; key: DraftTypeOption }[])),
  ];

  useEffect(() => {
    if (selectedDoc && selectedDoc !== processedDocRef.current) {
      const docName = docs.find((doc) => doc.id === selectedDoc)?.file.name;
      if (!docName) return;
      setTaggedDocuments([{ doc_id: selectedDoc, file_name: docName }]);
      onChange(`@[${docName}](${selectedDoc}) `);

      if (mentionsInputRef.current) {
        const textarea = mentionsInputRef.current;
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
        textarea.focus();
      }

      processedDocRef.current = selectedDoc;
    }
  }, [selectedDoc, docs, setTaggedDocuments, onChange]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim()) {
        executeSearch({ question: value, questionMode: mode, draftType });
      }
    }
  };

  const renderTruncatedMention = (id: string, display: string) => {
    return display.length > 20 ? `${display.substring(0, 20)}...` : display;
  };

  const handleChange = ({ newValue, mentions }: { newValue: string; mentions: MentionInterface[] }) => {
    const uniqueTaggedDocs = mentions.reduce(
      (acc, mention) => {
        if (!acc.some((doc) => doc.doc_id === mention.id)) {
          acc.push({ doc_id: mention.id, file_name: mention.display });
        }
        return acc;
      },
      [] as { doc_id: string; file_name: string }[],
    );

    setTaggedDocuments(uniqueTaggedDocs);
    onChange(newValue);
  };

  const triggerMentions = useCallback(() => {
    if (mentionsInputRef.current) {
      const textarea = mentionsInputRef.current;
      const currentValue = value;
      const cursorPosition = textarea.selectionStart;

      // Insert '@' at the cursor position
      const newValue = currentValue + '@';
      onChange(newValue);

      // Use requestAnimationFrame to ensure the DOM has updated
      requestAnimationFrame(() => {
        // Set cursor position after the inserted '@'
        textarea.setSelectionRange(cursorPosition + 1, cursorPosition + 1);
        textarea.focus();

        // Simulate typing '@' by dispatching events
        const inputEvent = new InputEvent('input', { bubbles: true });
        textarea.dispatchEvent(inputEvent);

        const keydownEvent = new KeyboardEvent('keydown', { key: '@', bubbles: true });
        textarea.dispatchEvent(keydownEvent);
      });
    }
  }, [onChange, value]);

  const sanitizeFileName = (fileName: string) => {
    return fileName.replace(/[[]()]/g, '');
  };

  const handleDraftTypeChange = (value: string) => {
    if (value === DraftTypeOption.WitnessStatement || value === DraftTypeOption.Deposition) {
      // Prefill the question and show document selection modal
      onChange('Create a deposition summary for...');
      setShowDocumentModal(true);
      setDraftType(DraftType.Deposition);
    } else {
      setDraftType(value as DraftType);
    }
  };

  const handleDocumentSelection = () => {
    setDraftType(DraftType.Deposition);
    setShowDocumentModal(false);
  };

  const toggleDocumentSelection = (doc: DocIds) => {
    const isAlreadyTagged = taggedDocuments.some((taggedDoc) => taggedDoc.doc_id === doc.id);

    if (isAlreadyTagged) {
      // Remove the document mention from the input
      const mentionToRemove = `@[${sanitizeFileName(doc.file.name)}](${doc.id})`;
      const newValue = value.replace(mentionToRemove, '').trim();
      onChange(newValue);

      // Update tagged documents
      const updatedTaggedDocs = taggedDocuments.filter((taggedDoc) => taggedDoc.doc_id !== doc.id);
      setTaggedDocuments(updatedTaggedDocs);
    } else {
      // Add the document mention to the input
      const mentionToAdd = `@[${sanitizeFileName(doc.file.name)}](${doc.id})`;
      const currentValue = value || 'Create a deposition summary for...';
      const newValue = currentValue.replace('...', `... ${mentionToAdd}`);
      onChange(newValue);

      // Update tagged documents
      const updatedTaggedDocs = [...taggedDocuments, { doc_id: doc.id, file_name: doc.file.name }];
      setTaggedDocuments(updatedTaggedDocs);
    }
  };

  return (
    <div className="relative flex w-full flex-col items-center gap-4">
      <div
        className={`flex w-full flex-col gap-1 rounded-md border px-2 pt-4 pb-2 transition-all duration-300 ${
          isFocused ? 'border-gray-300' : 'border-gray-200'
        }`}
      >
        <div className="relative min-h-20">
          <div className="max-h-56">
            <MentionsInput
              value={value}
              onChange={(e, newValue, newPlainTextValue, mentions) => handleChange({ newValue, mentions })}
              placeholder={placeholder}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              a11ySuggestionsListLabel={'Suggested mentions'}
              onKeyDown={onKeyDown}
              inputRef={mentionsInputRef}
              style={MentionsInputStyleKim}
            >
              <Mention
                trigger="@"
                data={taggableDocs.map((doc) => ({
                  id: doc.id,
                  display: sanitizeFileName(doc.file.name),
                }))}
                appendSpaceOnAdd={true}
                displayTransform={renderTruncatedMention}
                style={{
                  backgroundColor: '#EEEEEE',
                  borderRadius: '2px',
                }}
                onAdd={() => trackCustomEvent(TrackingEvents.KimTagDocument)}
              />
            </MentionsInput>
          </div>
          {mode === KimMode.DraftFlow && (
            <div className="my-2 w-fit">
              <Select onValueChange={handleDraftTypeChange}>
                <SelectTrigger className="text-xs">
                  <SelectValue placeholder="Draft type (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {draftTypeOptions.map((option) => (
                    <SelectItem key={option.key} value={option.key}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="flex items-end justify-between gap-2 pr-1">
          <button
            disabled={isCreatingThread}
            className="flex cursor-pointer items-center text-xs text-gray-500 italic hover:text-gray-700"
            onClick={triggerMentions}
          >
            @ Documents
          </button>
          <button
            disabled={isCreatingThread}
            className="bg-brandSecondary hover:bg-brandSecondary-hover flex h-7 w-7 cursor-pointer items-center justify-center rounded-full p-1 text-sm font-semibold text-white"
            onClick={() => executeSearch({ question: value, questionMode: mode, draftType })}
          >
            <span
              className={`iconify ${isCreatingThread ? 'lucide--loader-circle animate-spin' : 'lucide--arrow-right'} h-5 w-5 text-white`}
            ></span>
          </button>
        </div>
      </div>

      {/* Document Selection Modal */}
      <Modal
        content={
          <div className="p-6">
            <div className="mb-4">
              <p className="mb-2 text-sm text-gray-600">
                Select the documents you want to include in your summary. Only claim-related documents are available for
                this type of draft.
              </p>
              <div className="mb-4 text-xs text-gray-500">
                <strong>Available document types:</strong> {CLAIM_DOC_TYPES.join(', ')}
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto rounded-lg border border-gray-200">
              {taggableDocs.length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-500">
                  <p>No claim-related documents found.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {taggableDocs.map((doc) => (
                    <div
                      key={doc.id}
                      className={`cursor-pointer p-4 transition-colors hover:bg-gray-50 ${
                        taggedDocuments.some((taggedDoc) => taggedDoc.doc_id === doc.id)
                          ? 'bg-brandSecondary/5 border-brandSecondary border-l-4'
                          : ''
                      }`}
                      onClick={() => toggleDocumentSelection(doc)}
                    >
                      <div className="flex items-center">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{doc.file.name}</div>
                          <div className="mt-1 text-xs text-gray-500">Type: {doc.type}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {taggedDocuments.length} document{taggedDocuments.length !== 1 ? 's' : ''} selected
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Clear all tagged documents and reset the input
                    setTaggedDocuments([]);
                    onChange('');
                    setShowDocumentModal(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleDocumentSelection}
                  disabled={taggedDocuments.length === 0}
                >
                  Done
                </Button>
              </div>
            </div>
          </div>
        }
        isOpen={showDocumentModal}
        handleClose={() => {
          setShowDocumentModal(false);
        }}
        maxWidth="60rem"
      />
    </div>
  );
}
